import type { ClinicRole } from '@/types/auth';

export const journalPatientConfirmMembershipQueryKey = (
  clinicId: string | null | undefined,
  userId: string | null | undefined,
) => ['journal-patient-confirm-membership', clinicId ?? null, userId ?? null] as const;

export type JournalPatientConfirmRole = {
  id: string;
  system_key: string | null;
  require_journal_patient_confirm: boolean;
};

export type JournalPatientConfirmMember = {
  role: ClinicRole;
  custom_role_id: string | null;
  confirm_journal_patient: boolean;
};

export type JournalPatientConfirmState = {
  /** Stored personal on/off. The role toggle never writes this. */
  personal: boolean;
  /** Role requires the check. The employee checkbox is locked on. */
  forcedByRole: boolean;
  /** Finish flow must ask before the journal can be completed. */
  effective: boolean;
};

/**
 * Same role assignment as Behandler: clinic admins use the Klinikadministrator
 * role, a custom role wins for employees, otherwise the Medarbejder template.
 */
export function journalPatientConfirmRole(
  member: Pick<JournalPatientConfirmMember, 'role' | 'custom_role_id'>,
  roles: readonly JournalPatientConfirmRole[],
): JournalPatientConfirmRole | undefined {
  if (member.role === 'clinic_admin' || member.role === 'main_clinic_admin') {
    return roles.find((role) => role.system_key === 'clinic_admin');
  }
  if (member.custom_role_id) {
    return roles.find((role) => role.id === member.custom_role_id);
  }
  return roles.find((role) => role.system_key === 'employee');
}

export function journalPatientConfirmState(
  member: JournalPatientConfirmMember,
  roles: readonly JournalPatientConfirmRole[],
): JournalPatientConfirmState {
  const forcedByRole = Boolean(
    journalPatientConfirmRole(member, roles)?.require_journal_patient_confirm,
  );
  const personal = Boolean(member.confirm_journal_patient);
  return {
    personal,
    forcedByRole,
    effective: forcedByRole || personal,
  };
}

export function journalPatientConfirmSentence(
  patientName: string,
  lead: string,
  question: string,
): string {
  const name = patientName.trim() || '—';
  return `${lead} "${name}". ${question}`;
}
